import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ['summary', 'downloadButton']
  static values = {
    url: String,
    interval: { type: Number, default: 2000 }
  }

  connect() {
    this.disableDownload()
    this.poll()
  }

  disconnect() {
    this.stopPolling()
  }

  poll() {
    this.timer = setInterval(() => {
      this.checkStatus()
    }, this.intervalValue)
  }

  stopPolling() {
    if (this.timer) {
      clearInterval(this.timer)
    }
  }

  async checkStatus() {
    try {
      const response = await fetch(this.urlValue, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      if (!response.ok) return

      const data = await response.json()

      if (data.status === 'ready') {
        this.stopPolling()
        this.renderSummary(data.summary)
        this.enableDownload()
      }
    } catch (error) {
      console.error('AI Summary polling error:', error)
    }
  }

  disableDownload() {
    if (!this.hasDownloadButtonTarget) return
    const btn = this.downloadButtonTarget
    btn.dataset.originalHref = btn.getAttribute('href')
    btn.setAttribute('href', '#')
    btn.setAttribute('aria-disabled', 'true')
    btn.setAttribute('title', 'Aguarde a análise da IA terminar para baixar o PDF')
    btn.classList.add('opacity-50', 'cursor-not-allowed', 'pointer-events-none')
  }

  enableDownload() {
    if (!this.hasDownloadButtonTarget) return
    const btn = this.downloadButtonTarget
    if (btn.dataset.originalHref) {
      btn.setAttribute('href', btn.dataset.originalHref)
      delete btn.dataset.originalHref
    }
    btn.removeAttribute('aria-disabled')
    btn.removeAttribute('title')
    btn.classList.remove('opacity-50', 'cursor-not-allowed', 'pointer-events-none')
  }

  renderSummary(summary) {
    if (!this.hasSummaryTarget) return
    const target = this.summaryTarget
    target.className = ''

    const recommendationConfig = {
      'approve': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', label: 'Aprovar', icon: 'icon-[material-symbols-light--check-circle]' },
      'approve_with_caution': { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', label: 'Aprovar com Ressalvas', icon: 'icon-[material-symbols-light--warning]' },
      'reject': { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', label: 'Reprovar', icon: 'icon-[material-symbols-light--cancel]' }
    }

    const riskConfig = {
      'low': { bg: 'bg-green-50', text: 'text-green-700', label: 'Baixo Risco' },
      'medium': { bg: 'bg-yellow-50', text: 'text-yellow-700', label: 'Risco Moderado' },
      'high': { bg: 'bg-red-50', text: 'text-red-700', label: 'Alto Risco' }
    }

    const rec = recommendationConfig[summary.recommendation] || recommendationConfig['approve_with_caution']
    const risk = riskConfig[summary.risk_level] || riskConfig['medium']

    let redFlagsHtml = ''
    if (summary.red_flags && summary.red_flags.length > 0) {
      redFlagsHtml = `
        <div class="mb-4">
          <div class="flex items-center gap-2 mb-2">
            <span class="icon-[material-symbols-light--warning] w-5 h-5 text-red-500"></span>
            <h3 class="text-sm font-bold text-red-700">Alertas</h3>
          </div>
          <ul class="space-y-1">
            ${summary.red_flags.map(flag => `
              <li class="flex items-start gap-2 text-xs text-red-600">
                <span class="icon-[material-symbols-light--close] w-4 h-4 text-red-500 flex-shrink-0 mt-0.5"></span>
                ${this.escapeHtml(flag)}
              </li>
            `).join('')}
          </ul>
        </div>
      `
    }

    let positiveFactorsHtml = ''
    if (summary.positive_factors && summary.positive_factors.length > 0) {
      positiveFactorsHtml = `
        <div class="mb-4">
          <div class="flex items-center gap-2 mb-2">
            <span class="icon-[material-symbols-light--thumb-up] w-5 h-5 text-green-500"></span>
            <h3 class="text-sm font-bold text-green-700">Pontos Positivos</h3>
          </div>
          <ul class="space-y-1">
            ${summary.positive_factors.map(factor => `
              <li class="flex items-start gap-2 text-xs text-green-600">
                <span class="icon-[material-symbols-light--check] w-4 h-4 text-green-500 flex-shrink-0 mt-0.5"></span>
                ${this.escapeHtml(factor)}
              </li>
            `).join('')}
          </ul>
        </div>
      `
    }

    target.innerHTML = `
      <div class="bg-white rounded-xl shadow-lg p-6 border border-purple-200">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-2">
            <span class="icon-[material-symbols-light--auto-awesome] w-6 h-6 text-purple-600"></span>
            <h2 class="text-lg font-bold text-gray-900">Análise de Crédito</h2>
            <span class="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">IA</span>
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-2 mb-4">
          <div class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg ${rec.bg} ${rec.text} border ${rec.border} text-sm font-semibold">
            <span class="${rec.icon} w-4 h-4"></span>
            ${rec.label}
          </div>
          <span class="inline-flex items-center px-2.5 py-1 rounded-full ${risk.bg} ${risk.text} text-xs font-medium">
            ${risk.label}
          </span>
        </div>

        <div class="mb-4 p-4 bg-gray-50 rounded-lg">
          <p class="text-sm text-gray-700 leading-relaxed">${this.escapeHtml(summary.summary)}</p>
        </div>

        ${redFlagsHtml}
        ${positiveFactorsHtml}

        <div class="mt-4 p-3 bg-gray-100 rounded-lg border border-gray-200">
          <div class="flex items-start gap-2">
            <span class="icon-[material-symbols-light--info] w-4 h-4 text-gray-500 mt-0.5"></span>
            <p class="text-xs text-gray-500">
              Esta análise foi gerada por IA e <strong>não é conclusiva</strong>. Revise os dados completos antes de tomar decisões.
            </p>
          </div>
        </div>
      </div>
    `
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
